/* eslint-disable @department-of-veterans-affairs/prefer-button-component */
/* eslint-disable camelcase */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  browserSupportsWebAuthn,
  startRegistration,
} from '@simplewebauthn/browser';
import environment from 'platform/utilities/environment';
import { AUTHN_SETTINGS } from 'platform/user/authentication/constants';
import { apiRequest } from 'platform/utilities/api';
import {
  authenticateWithPasskey,
  clearPasskeyOriginClient,
  getPasskeyErrorMessage,
  getPasskeyOriginClient,
  requestPasskeySignInState,
} from '../passkeys';
import { PasskeyAlert, usePasskeyAlert } from './PasskeyAlertSlot';

const PASSKEY_API_URL = environment.API_URL;
const PASSKEY_REGISTRATION_OPTIONS_URL = `${PASSKEY_API_URL}/sign_in/webauthn/registrations/options`;
const PASSKEY_REGISTRATION_VERIFY_URL = `${PASSKEY_API_URL}/sign_in/webauthn/registrations/verify`;

const COPY = {
  unsupportedBody:
    'We’re sorry, but your current device or browser doesn’t support passkeys creation.',
  registerHeading: 'Create a passkey',
  registerDescription:
    'Follow the steps on your browser or device to set up a new passkey.',
  registerButtonLabel: 'Create a new passkey',
  registerButtonLoading: 'Creating passkey...',
  alreadyRegistered:
    'This device already has a passkey registered for your account.',
  registrationSuccess: 'You can use it the next time you sign in to VA.gov.',
  successHeading: 'You successfully created a passkey',
  registrationError:
    'There was an error creating your passkey. Please try again.',
  verificationError:
    'Something went wrong while verifying your passkey. Please try again.',
  verificationFetchError:
    'There was an error verifying your passkey registration. Please try again.',
  unexpectedError: 'An unexpected error occurred. Please try again.',
  errorHeading: 'Something went wrong and your passkey wasn’t created',
  errorBody: 'You can try again, or try using a different browser or device.',
  errorLinkText: 'Learn how to troubleshoot common passkey issues',
  returnToClientLink: 'Continue',
  returnFailedHeading: 'Your passkey was created',
  returnFailedBody: 'Sign in with your new passkey to continue.',
  signInWithPasskeyLabel: 'Sign in with your passkey',
  signInWithPasskeyLoading: 'Signing in with passkey...',
};

const ERROR_LINK_HREF = '/resources/support-for-common-passkey-issues';

const PasskeyRegistration = ({ onRegistered, dismissSignal = 0 }) => {
  // { message, type, key } kept in one state value: React 17 doesn't batch
  // updates after an await, so separate setters briefly rendered the error
  // alert before switching to success and remounted the alert.
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [returnUrl, setReturnUrl] = useState(null);
  const [returnFailed, setReturnFailed] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [signInError, setSignInError] = useState('');
  const resultKeyRef = useRef(0);
  const sharedAlert = usePasskeyAlert();

  const postLogin =
    new URLSearchParams(window.location.search).get('postLogin') === 'true';
  const priorReturnUrl = sessionStorage.getItem(
    AUTHN_SETTINGS.PASSKEY_PRIOR_RETURN_URL,
  );

  const [originClient] = useState(() =>
    postLogin ? getPasskeyOriginClient() : null,
  );

  const originClientParams = () => ({
    application: originClient.application,
    clientId: originClient.clientId,
    queryParams: {
      codeChallenge: originClient.codeChallenge,
      state: originClient.state,
      scope: originClient.scope,
      authorizeSsoId: originClient.authorizeSsoId,
    },
  });

  const requestOriginClientState = () =>
    requestPasskeySignInState(originClientParams()).then(
      ({ signedState }) => signedState,
    );

  const returnToOriginClient = redirectUrl => {
    clearPasskeyOriginClient();
    setReturnUrl(redirectUrl);
    window.location.replace(redirectUrl);
  };

  const handleSignInWithPasskey = async () => {
    setIsSigningIn(true);
    setSignInError('');

    try {
      const { redirectUrl } = await authenticateWithPasskey(
        originClientParams(),
      );
      returnToOriginClient(redirectUrl);
    } catch (error) {
      setSignInError(getPasskeyErrorMessage(error));
    } finally {
      setIsSigningIn(false);
    }
  };

  const showMessage = (text, type = 'info') => {
    resultKeyRef.current += 1;
    // A new key remounts PasskeyAlert so focus moves to it exactly once.
    setResult({ message: text, type, key: resultKeyRef.current });
  };

  const clearMessage = () => setResult(null);

  const isSuccess = result?.type === 'success';

  const alertNode =
    result &&
    (isSuccess ? (
      <PasskeyAlert
        key={result.key}
        status={returnFailed && !returnUrl ? 'warning' : 'success'}
        headline={
          returnFailed && !returnUrl
            ? COPY.returnFailedHeading
            : COPY.successHeading
        }
      >
        <p className="vads-u-margin-y--0">{result.message}</p>
        {returnUrl && (
          <va-link active href={returnUrl} text={COPY.returnToClientLink} />
        )}
        {returnFailed && !returnUrl && (
          <>
            <p className="vads-u-margin-y--0">{COPY.returnFailedBody}</p>
            <va-button
              class="vads-u-margin-top--2"
              text={
                isSigningIn
                  ? COPY.signInWithPasskeyLoading
                  : COPY.signInWithPasskeyLabel
              }
              onClick={handleSignInWithPasskey}
              disabled={isSigningIn || undefined}
              secondary
            />
            {signInError && (
              <p className="vads-u-margin-y--0" role="alert">
                {signInError}
              </p>
            )}
          </>
        )}
        {!originClient && postLogin && priorReturnUrl && (
          <va-link active href={priorReturnUrl} text="Continue on VA.gov" />
        )}
      </PasskeyAlert>
    ) : (
      <PasskeyAlert
        key={result.key}
        status="error"
        headline={COPY.errorHeading}
      >
        <p className="vads-u-margin-y--0">{COPY.errorBody}</p>
        <va-link href={ERROR_LINK_HREF} text={COPY.errorLinkText} />
        {alreadyRegistered &&
          originClient &&
          (returnUrl ? (
            <va-link active href={returnUrl} text={COPY.returnToClientLink} />
          ) : (
            <va-button
              class="vads-u-margin-top--2"
              text={
                isSigningIn
                  ? COPY.signInWithPasskeyLoading
                  : COPY.signInWithPasskeyLabel
              }
              onClick={handleSignInWithPasskey}
              disabled={isSigningIn || undefined}
              secondary
            />
          ))}
        {signInError && (
          <p className="vads-u-margin-y--0" role="alert">
            {signInError}
          </p>
        )}
      </PasskeyAlert>
    ));

  // Push into the shared alert region when present so alerts replace, not stack.
  const alertNodeRef = useRef(alertNode);
  alertNodeRef.current = alertNode;
  useEffect(() => {
    if (!sharedAlert) return undefined;
    sharedAlert.setAlert(alertNodeRef.current);
    return () => sharedAlert.setAlert(null);
  }, [
    sharedAlert,
    result,
    returnUrl,
    returnFailed,
    alreadyRegistered,
    isSigningIn,
    signInError,
  ]);

  // Clear a lingering alert when another passkey flow (e.g. delete) starts so
  // a stale result isn't left above the new flow.
  useEffect(() => {
    if (dismissSignal) setResult(null);
  }, [dismissSignal]);

  const handleRegisterPasskey = async e => {
    e.preventDefault();
    setIsLoading(true);
    clearMessage();
    setReturnFailed(false);
    setAlreadyRegistered(false);
    setSignInError('');

    try {
      const [{ options, challengeId }, signedState] = await Promise.all([
        apiRequest(PASSKEY_REGISTRATION_OPTIONS_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }),
        originClient ? requestOriginClientState() : Promise.resolve(null),
      ]);

      let registrationData;

      try {
        registrationData = await startRegistration({ optionsJSON: options });
      } catch (error) {
        if (error.name === 'InvalidStateError') {
          setAlreadyRegistered(true);
          showMessage(COPY.alreadyRegistered, 'warning');
          return;
        }
        if (!browserSupportsWebAuthn()) {
          showMessage(COPY.unsupportedBody, 'error');
          return;
        }
        showMessage(COPY.registrationError, 'error');
        return;
      }

      try {
        const verifyResponse = await apiRequest(
          PASSKEY_REGISTRATION_VERIFY_URL,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Key-Inflection': null,
            },
            body: JSON.stringify({
              registration: registrationData,
              challenge_id: challengeId,
              ...(signedState && { sign_in_state: signedState }),
            }),
          },
        );

        if (verifyResponse?.verified) {
          const redirectUrl = signedState && verifyResponse.redirect_url;
          if (originClient && !redirectUrl) setReturnFailed(true);
          showMessage(COPY.registrationSuccess, 'success');
          onRegistered?.();
          if (redirectUrl) returnToOriginClient(redirectUrl);
        } else {
          showMessage(COPY.verificationError, 'error');
        }
      } catch {
        showMessage(COPY.verificationFetchError, 'error');
      }
    } catch {
      showMessage(COPY.unexpectedError, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="passkeys-registration">
      <h2>{COPY.registerHeading}</h2>

      {!sharedAlert && alertNode}
      <div className="passkeys-content">
        <p>{COPY.registerDescription}</p>

        <va-button
          data-testid="create-passkey-button"
          onClick={handleRegisterPasskey}
          loading={isLoading}
          text={
            isLoading ? COPY.registerButtonLoading : COPY.registerButtonLabel
          }
        />
      </div>
    </div>
  );
};

PasskeyRegistration.propTypes = {
  dismissSignal: PropTypes.number,
  onRegistered: PropTypes.func,
};

export default PasskeyRegistration;
